import { useMutation } from '@tanstack/react-query';
import {
  Action,
  Banner,
  ConfirmModal,
  List,
  Modal,
  TextAreaInput,
} from 'corso-admin-ui';
import { ReactNode, useId, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { match, P } from 'ts-pattern';
import { useLocalStorage, useSessionStorage } from 'usehooks-ts';
import { useToast } from '~/hooks/useToast';
import { useStickySearchParams } from '~/providers/StickySearchParams';
import { useStoreUser } from '~/providers/StoreUserProvider';
import {
  sendAndStopDebugReplay,
  startDebugReplay,
  stopDebugReplay,
} from '~/utils/observability';

const MAX_USED_REQUESTS = 10;

type OfferedDialog = { name: 'start' } | { name: 'cancel' };
type RecordingDialog = { name: 'stop' } | { name: 'send'; details: string };
type Dialog = OfferedDialog | RecordingDialog | null;

type ReplayState =
  | { status: 'idle' }
  | { status: 'offered'; request: string; dialog: OfferedDialog | null }
  | { status: 'recording'; dialog: RecordingDialog | null };

type ReplaySources = {
  recording: boolean;
  request: string | null;
  dialog: Dialog;
};

function deriveReplayState(sources: ReplaySources) {
  // a dialog left open from another stage is ignored rather than shown
  return match<ReplaySources, ReplayState>(sources)
    .with(
      { recording: true, dialog: { name: P.union('stop', 'send') } },
      ({ dialog }) => ({ status: 'recording', dialog }),
    )
    .with({ recording: true }, () => ({ status: 'recording', dialog: null }))
    .with(
      {
        recording: false,
        request: P.string,
        dialog: { name: P.union('start', 'cancel') },
      },
      ({ request, dialog }) => ({ status: 'offered', request, dialog }),
    )
    .with({ recording: false, request: P.string }, ({ request }) => ({
      status: 'offered',
      request,
      dialog: null,
    }))
    .with({ recording: false, request: P.nullish }, () => ({ status: 'idle' }))
    .exhaustive();
}

function BannerAction({
  onClick,
  children,
}: {
  onClick: () => void;
  children: NonNullable<ReactNode>;
}) {
  return (
    <Action
      variant="link"
      // TODO evaluate an alternative link variant to handle cases where link needs to contrast using white
      className="text-white underline hover:text-white active:text-white"
      onClick={onClick}
    >
      {children}
    </Action>
  );
}

export default function DebugReplayBanner() {
  // session-scoped to match Sentry's sticky replay session, which ends with the tab
  const [recording, setRecording] = useSessionStorage(
    'corso.debugReplay.recording',
    false,
  );

  // each request link only triggers once, so a later browser autocomplete of the URL doesn't re-prompt
  const [usedRequests, setUsedRequests] = useLocalStorage<string[]>(
    'corso.debugReplay.usedRequests',
    [],
  );

  // sticky so the request survives navigating to pages without `?r=`
  const stickyRequest = useStickySearchParams().get('r');
  const [, setSearchParams] = useSearchParams();
  const [dialog, setDialog] = useState<Dialog>({ name: 'start' });
  const detailsId = useId();
  const storeName = useStoreUser().store.name;
  const toast = useToast();

  const state = deriveReplayState({
    recording,
    request:
      stickyRequest && !usedRequests.includes(stickyRequest)
        ? stickyRequest
        : null,
    dialog,
  });
  const openDialog = state.status === 'idle' ? null : state.dialog;
  const details = openDialog?.name === 'send' ? openDialog.details : '';

  const closeDialog = () => setDialog(null);

  const consumeRequest = () => {
    if (state.status !== 'offered') return;
    const offeredRequest = state.request;
    setUsedRequests((used) =>
      [...used, offeredRequest].slice(-MAX_USED_REQUESTS),
    );
    setSearchParams(
      (params) => {
        params.delete('r');
        return params;
      },
      { replace: true },
    );
    closeDialog();
  };

  const handleStart = () => {
    startDebugReplay();
    setRecording(true);
    consumeRequest();
  };

  const finishRecording = () => {
    setRecording(false);
    closeDialog();
  };

  const sendReplay = useMutation({
    mutationFn: (message: string) =>
      sendAndStopDebugReplay({ storeName, message }),
    onSuccess: () => {
      finishRecording();
      toast.show('Recording sent.');
    },
    onError: (error) => {
      console.error(error);
      toast.show("Recording couldn't be sent. Please try again.");
    },
  });

  const stopReplay = useMutation({
    mutationFn: stopDebugReplay,
    onSuccess: () => {
      finishRecording();
      toast.show('Recording stopped.');
    },
    onError: (error) => {
      console.error(error);
      toast.show("Recording couldn't be stopped. Please try again.");
    },
  });

  return (
    <>
      <Modal
        show={openDialog?.name === 'start'}
        onClose={closeDialog}
        title="Record this session?"
        description="A recording of your session, including what you type, will be shared with Corso support."
        actions={
          <>
            <Action onClick={closeDialog}>Not Now</Action>
            <Action variant="primary" onClick={handleStart}>
              Start Recording
            </Action>
          </>
        }
      >
        <List
          as="ol"
          columns={1}
          items={[
            'Click Start Recording',
            'Reproduce the issue',
            'Click Finish Recording in the banner at the top of the page',
          ]}
        >
          {(item) => <List.NumberedItem key={item}>{item}</List.NumberedItem>}
        </List>
      </Modal>
      <Modal
        show={openDialog?.name === 'send'}
        onClose={closeDialog}
        title="Send recording?"
        description="The recording of your session will be shared with Corso support."
        actions={
          <>
            <Action onClick={closeDialog}>Keep Recording</Action>
            <Action
              variant="primary"
              loading={sendReplay.isPending}
              onClick={() => sendReplay.mutate(details)}
            >
              Send Report
            </Action>
          </>
        }
      >
        <TextAreaInput
          id={detailsId}
          label="Additional details?"
          value={details}
          onChange={(e) => setDialog({ name: 'send', details: e.target.value })}
        />
      </Modal>
      <ConfirmModal
        show={openDialog?.name === 'stop'}
        onCancel={closeDialog}
        onConfirm={() => stopReplay.mutate()}
        loading={stopReplay.isPending}
        title="Stop recording?"
        prompt="Recording will be stopped, and nothing will be shared with Corso."
        cancelText="Keep Recording"
        confirmText="Stop Recording"
        variant="caution"
      />
      <ConfirmModal
        show={openDialog?.name === 'cancel'}
        onCancel={closeDialog}
        onConfirm={consumeRequest}
        title="Cancel recording request?"
        prompt="This link won't ask you to record again."
        cancelText="Go Back"
        confirmText="Cancel Request"
        variant="caution"
      />

      {match(state)
        .with(
          { status: 'idle' },
          { status: 'offered', dialog: { name: 'start' } },
          () => null,
        )
        .with({ status: 'offered' }, () => (
          <Banner
            variant="warning"
            dismissible
            // dismissing only opens a confirmation, so stay visible if it's backed out of
            dismissed={false}
            onDismiss={() => setDialog({ name: 'cancel' })}
          >
            <div className="flex items-center gap-2">
              <p>Corso support has asked you to record this session.</p>
              <BannerAction onClick={() => setDialog({ name: 'start' })}>
                Start Recording
              </BannerAction>
            </div>
          </Banner>
        ))
        .with({ status: 'recording' }, () => (
          <Banner
            variant="warning"
            dismissible
            dismissed={false}
            onDismiss={() => setDialog({ name: 'stop' })}
          >
            <div className="flex items-center gap-2">
              <span className="relative flex size-2.5" aria-hidden="true">
                <span className="absolute inline-flex size-full rounded-full bg-white opacity-75 motion-safe:animate-ping" />
                <span className="relative inline-flex size-2.5 rounded-full bg-white" />
              </span>
              <p>Recording this session for Corso support.</p>
              <BannerAction
                onClick={() => setDialog({ name: 'send', details: '' })}
              >
                Finish Recording
              </BannerAction>
            </div>
          </Banner>
        ))
        .exhaustive()}
    </>
  );
}
